'use client';

import { Search, Filter, Download, Plus } from 'lucide-react';

interface PromotersCRMWidgetProps {
  className?: string;
}

// Mock promoter data matching the real app structure
interface MockPromoter {
  id: string;
  name: string;
  initials: string;
  city: string;
  sector: string;
  isActive: boolean;
  projectsCount: number;
  cupoGenerado: number;
  completedAmount: number;
  reservedAmount: number;
  availableAmount: number;
}

// Realistic promoter data
const promoters: MockPromoter[] = [
  {
    id: 'P001',
    name: 'Aurora Films S.L.',
    initials: 'AF',
    city: 'Madrid',
    sector: 'Cine',
    isActive: true,
    projectsCount: 8,
    cupoGenerado: 450000,
    completedAmount: 180000,
    reservedAmount: 135000,
    availableAmount: 135000,
  },
  {
    id: 'P002',
    name: 'Velvet Music Productions',
    initials: 'VM',
    city: 'Barcelona',
    sector: 'Musica',
    isActive: true,
    projectsCount: 5,
    cupoGenerado: 280000,
    completedAmount: 112000,
    reservedAmount: 56000,
    availableAmount: 112000,
  },
  {
    id: 'P003',
    name: 'Stellar Entertainment',
    initials: 'SE',
    city: 'Valencia',
    sector: 'Artes escenicas',
    isActive: true,
    projectsCount: 3,
    cupoGenerado: 175000,
    completedAmount: 52500,
    reservedAmount: 70000,
    availableAmount: 52500,
  },
  {
    id: 'P004',
    name: 'Nova Cine Studios',
    initials: 'NC',
    city: 'Sevilla',
    sector: 'Cine',
    isActive: false,
    projectsCount: 2,
    cupoGenerado: 120000,
    completedAmount: 96000,
    reservedAmount: 0,
    availableAmount: 24000,
  },
];

// Format currency Spanish style
const formatCurrency = (amount: number): string => {
  const formattedNumber = new Intl.NumberFormat('es-ES', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
    useGrouping: true,
  }).format(amount);
  return `${formattedNumber} €`;
};

// Quota bar colors (matching the app exactly)
const quotaBarColors = {
  completed: '#27272a', // zinc-800
  reserved: '#8b5cf6', // violet-500
  available: '#e4e4e7', // zinc-200
};

export function PromotersCRMWidget({ className }: PromotersCRMWidgetProps) {
  const renderQuotaBar = (promoter: MockPromoter) => {
    const total = promoter.cupoGenerado;
    const completedPercentage =
      total > 0 ? Math.round((promoter.completedAmount / total) * 100) : 0;
    const reservedPercentage = total > 0 ? Math.round((promoter.reservedAmount / total) * 100) : 0;
    const totalOccupied = completedPercentage + reservedPercentage;

    return (
      <div className="flex items-center justify-center">
        <div
          className="w-16 rounded-full h-2 mr-2 relative overflow-hidden"
          style={{ backgroundColor: quotaBarColors.available }}
        >
          <div
            className="h-2 absolute left-0"
            style={{
              backgroundColor: quotaBarColors.completed,
              width: `${completedPercentage}%`,
            }}
          />
          <div
            className="h-2 absolute"
            style={{
              backgroundColor: quotaBarColors.reserved,
              width: `${reservedPercentage}%`,
              left: `${completedPercentage}%`,
            }}
          />
        </div>
        <span className="text-xs font-medium text-[#71717a] w-8">{totalOccupied}%</span>
      </div>
    );
  };

  const renderRow = (promoter: MockPromoter, _index: number, isLast: boolean) => {
    const subtitle = [promoter.city, promoter.sector].filter(Boolean).join(' • ');

    return (
      <tr
        key={promoter.id}
        className={`
          hover:bg-[#fafafa] cursor-pointer
          ${!isLast ? 'border-b border-[#e4e4e7]' : ''}
        `}
      >
        {/* Promotor column */}
        <td className="px-4 py-3 whitespace-nowrap text-xs text-left" style={{ width: '200px' }}>
          <div className="flex items-center min-w-0">
            <div className="relative flex-shrink-0 h-7 w-7">
              <div className="h-7 w-7 rounded bg-[#e4e4e7] flex items-center justify-center">
                <span className="text-xs font-medium text-[#52525b]">{promoter.initials}</span>
              </div>
              {/* Status indicator dot */}
              <div
                className={`absolute -bottom-0.5 -right-0.5 h-2.5 w-2.5 rounded-full border-2 border-white ${
                  promoter.isActive ? 'bg-[#22c55e]' : 'bg-[#a1a1aa]'
                }`}
              />
            </div>
            <div className="ml-2 min-w-0 flex-1">
              <div className="text-xs font-semibold text-[#0a0a0a] truncate">{promoter.name}</div>
              <div className="text-xs text-[#71717a] truncate">{subtitle}</div>
            </div>
          </div>
        </td>

        {/* Proyectos column */}
        <td
          className="px-4 py-3 whitespace-nowrap text-xs text-center"
          style={{ minWidth: '80px' }}
        >
          <div className="text-xs text-[#0a0a0a] font-medium">{promoter.projectsCount}</div>
        </td>

        {/* Cupo Generado column */}
        <td
          className="px-4 py-3 whitespace-nowrap text-xs text-center"
          style={{ minWidth: '120px' }}
        >
          <div className="text-xs text-[#0a0a0a] font-medium">
            {formatCurrency(promoter.cupoGenerado)}
          </div>
        </td>

        {/* Estado de Inversion column */}
        <td
          className="px-4 py-3 whitespace-nowrap text-xs text-center"
          style={{ minWidth: '120px' }}
        >
          {renderQuotaBar(promoter)}
        </td>
      </tr>
    );
  };

  return (
    <div className={className}>
      <div
        className="relative mx-auto"
        style={{
          maxWidth: '660px',
        }}
      >
        {/* DataTable container - exact match of app styling */}
        <div
          className="bg-white border border-[#e4e4e7] rounded-lg px-4 pb-2.5 pt-4"
          style={{
            boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08), 0 1px 3px rgba(0, 0, 0, 0.04)',
          }}
        >
          {/* TableActionsBar - exact match */}
          <div className="flex items-center justify-between gap-2 mb-3">
            <div className="flex items-center gap-2">
              {/* Search input */}
              <div className="relative">
                <Search
                  className="absolute left-2 top-1/2 transform -translate-y-1/2 text-[#3f3f46]"
                  size={12}
                  strokeWidth={2.5}
                />
                <input
                  type="text"
                  placeholder="Buscar promotor..."
                  readOnly
                  autoComplete="off"
                  tabIndex={-1}
                  className="pl-6 pr-2.5 py-1.5 w-48 text-xs border border-[#e4e4e7] rounded-md bg-white focus:outline-none placeholder:text-[#a1a1aa]"
                  style={{ boxShadow: '0 1px 2px 0 rgba(0, 0, 0, 0.05)' }}
                />
              </div>

              {/* Filter button */}
              <button className="flex items-center gap-1 px-2.5 py-1.5 text-xs font-medium border border-[#e4e4e7] rounded-md shadow-sm text-[#3f3f46] bg-white">
                <Filter size={12} strokeWidth={2.5} />
                Filtros
              </button>

              {/* Export/Download button */}
              <button
                className="flex items-center justify-center h-[30px] w-[30px] text-xs font-medium border border-[#e4e4e7] rounded-md shadow-sm text-[#3f3f46] bg-white hover:bg-[#fafafa]"
                title="Exportar"
              >
                <Download size={12} strokeWidth={2.5} />
              </button>
            </div>

            {/* Add button */}
            <button className="flex items-center gap-1 px-3 py-1.5 text-xs font-medium text-white bg-[#18181b] rounded-md hover:bg-[#27272a]">
              <Plus size={12} />
              Nuevo Promotor
            </button>
          </div>

          {/* Table container */}
          <div className="-mx-4 mb-2.5 border-b border-t border-[#e4e4e7]">
            <div className="overflow-x-auto">
              <table className="w-full">
                {/* Table header - exact match of app styling */}
                <thead className="bg-[#fafaf9] border-b border-[#e4e4e7]">
                  <tr>
                    <th
                      className="px-4 py-2.5 text-left text-xs font-medium text-[#71717a] tracking-wider"
                      style={{ width: '200px' }}
                    >
                      Promotor
                    </th>
                    <th
                      className="px-4 py-2.5 text-center text-xs font-medium text-[#71717a] tracking-wider"
                      style={{ minWidth: '80px' }}
                    >
                      Proyectos
                    </th>
                    <th
                      className="px-4 py-2.5 text-center text-xs font-medium text-[#71717a] tracking-wider"
                      style={{ minWidth: '120px' }}
                    >
                      Cupo Generado
                    </th>
                    <th
                      className="px-4 py-2.5 text-center text-xs font-medium text-[#71717a] tracking-wider"
                      style={{ minWidth: '120px' }}
                    >
                      Estado Inversion
                    </th>
                  </tr>
                </thead>

                {/* Table body */}
                <tbody className="bg-white">
                  {promoters.map((promoter, index) =>
                    renderRow(promoter, index, index === promoters.length - 1)
                  )}
                </tbody>
              </table>
            </div>
          </div>

          {/* Pagination footer - simplified version */}
          <div className="flex items-center justify-between text-xs text-[#52525b]">
            <span>
              Mostrando <span className="font-medium">1</span> a{' '}
              <span className="font-medium">{promoters.length}</span> de{' '}
              <span className="font-medium">{promoters.length}</span> resultados
            </span>
            <div className="flex items-center gap-1">
              <button className="px-2 py-1 border border-[#e4e4e7] rounded text-[#a1a1aa] cursor-not-allowed">
                Anterior
              </button>
              <button className="px-2 py-1 border border-[#e4e4e7] rounded bg-[#18181b] text-white">
                1
              </button>
              <button className="px-2 py-1 border border-[#e4e4e7] rounded hover:bg-[#fafafa]">
                Siguiente
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default PromotersCRMWidget;
